import clsx from 'clsx';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { MIME_TYPE_IMAGE_JPEG, MIME_TYPE_VIDEO_MP4 } from '@vestico/utils';

import { ClientReviewResponse, ImageSize, WidgetItemAspectRatio } from '@vestico/model';

import {
  Button,
  Carousel,
  CustomCSSProperties,
  DragMove,
  GlobalsContext,
  ImageDisplay,
  MediaViewerMobile,
  Movement,
  ProductsBannerMini,
  RenderArrowProps,
  Reveal,
  Spinner,
  SpinnerPositioning,
  SpinnerSize,
  VideoDisplay,
} from '@vestico/shared';

import { getIsRTL } from '@vestico/i18n';

import { NoResultsPlaceholder } from '../../../components/NoResultsPlaceholder';
import { AddToBag } from '../../../components/OverlayIcons/AddToBag';
import { OverlayIcon } from '../../../components/OverlayIcons/OverlayIcon';
import { PostLikeHeart } from '../../../components/OverlayIcons/PostLikeHeart';
import { SizeRecIcon } from '../../../components/OverlayIcons/SizeRecIcon';
import { ReviewDetails } from '../../../components/ReviewDetails';
import { useAddToBagActions } from '../../../hooks/useAddToBagActions';
import { AvailableMatchingType } from '../../../model/matchingType';
import { CarouselArrow } from './CarouselArrow';
import { CarouselContentProps } from './CarouselContent';
import { CarouselMobileProductsModal } from './CarouselMobile+Products';
import { CarouselMobileSizeRecModal } from './CarouselMobile+SizeRec';
import style from './CarouselMobile.module.css';

const IMAGE_BORDER_RADIUS = ['0', '0', '0', '0'];

export interface CarouselMobileProps {
  onClose: () => void;
}

export const CarouselMobile = ({
  responseIndex,
  responses,
  products,
  onProductLinkClick,
  onBeginUserMatchInfo,
  onResponseIndexChange,
  onClose,
  showBeginUserMatchInfoSection,
  showResetUserButton,
  anyResponseHasMatch,
  sizeRecommendation,
  activeMatchingType,
  onActiveMatchingTypeChange,
  hasContentMatchAvailable,
  hasSizeRecAvailable,
  defaultMatchingType,
}: CarouselMobileProps & CarouselContentProps) => {
  const [initialResponseIndex] = useState(responseIndex);
  const [didChangeIndex, setDidChangeIndex] = useState(false);

  const { t } = useTranslation();
  useEffect(() => {
    if (responseIndex !== initialResponseIndex) {
      setDidChangeIndex(true);
    }
  }, [responseIndex, initialResponseIndex]);

  const response: ClientReviewResponse | undefined = useMemo(
    () => (responses && responseIndex !== undefined ? responses[responseIndex] : undefined),
    [responses, responseIndex]
  );

  const videoOverlayRef = useRef<HTMLDivElement>(null);

  const [animatingResponseIndex, setAnimatingResponseIndex] = useState<number | undefined>(undefined);

  const singleResponseProduct = useMemo(() => {
    if (response && response.products.length === 1) {
      if (products === null) {
        return null;
      }
      const firstProduct = products?.[0];
      return firstProduct;
    }
    return null;
  }, [products, response]);
  const { addToBagEnabled } = useAddToBagActions({ product: singleResponseProduct });

  const [controlsHidden, setControlsHidden] = useState(false);
  const [revealCollapsed, setRevealCollapsed] = useState(true);

  const onBottomDragStart = useCallback(() => {
    setControlsHidden(true);
  }, []);

  const onBottomDragEnd = useCallback((collapsed: boolean) => {
    setRevealCollapsed(collapsed);
    setControlsHidden(!collapsed);
  }, []);

  const onWillChangeIndex = useCallback((pageIndex: number) => {
    setAnimatingResponseIndex(pageIndex);
  }, []);

  const onDidChangeIndex = useCallback(
    (pageIndex: number) => {
      if (onResponseIndexChange) {
        onResponseIndexChange(pageIndex);
      }

      setAnimatingResponseIndex(undefined);
    },
    [onResponseIndexChange]
  );

  const onActiveMatchingTypeToggle = useCallback(
    (type: AvailableMatchingType) => () => onActiveMatchingTypeChange(type),
    [onActiveMatchingTypeChange]
  );

  const [contentScrolledToTop, setContentScrolledToTop] = useState(true);
  const [contentIsScrollable, setContentIsScrollable] = useState(false);
  const onContentScrollTop = useCallback((scrolledToTop: boolean) => setContentScrolledToTop(scrolledToTop), []);
  const onContentIsScrollable = useCallback((isScrollable: boolean) => setContentIsScrollable(isScrollable), []);

  const [reviewContentIsEmpty, setReviewContentIsEmpty] = useState(false);
  const onReviewContentIsEmpty = useCallback((contentIsEmpty: boolean) => setReviewContentIsEmpty(contentIsEmpty), []);

  const [videoControlsVisible, setVideoControlsVisible] = useState(false);
  const [videoControlProgressBarHeight, setVideoControlProgressBarHeight] = useState(0);

  const onVideoControlsHide = useCallback((hide: boolean, newVideoControlProgressBarHeight: number) => {
    setVideoControlsVisible(!hide);
    setVideoControlProgressBarHeight(newVideoControlProgressBarHeight);
  }, []);

  const onZoomStart = useCallback(() => {
    setControlsHidden(true);
  }, []);

  const onZoomEnd = useCallback(() => {
    setControlsHidden(!revealCollapsed);
  }, [revealCollapsed]);

  const { stylesConfig } = useContext(GlobalsContext);
  const { likesEnabled } = stylesConfig.stylesConfig;

  const [topHeight, setTopHeight] = useState(0);
  const onTopHeightChange = useCallback((newTopHeight: number) => setTopHeight(newTopHeight), []);

  const revealAspectRatio: WidgetItemAspectRatio = useMemo(() => '9:16', []);

  // We use a less aggressive content aspect ratio to ensure content is placed better;
  const contentAspectRatio: WidgetItemAspectRatio = useMemo(() => '3:4', []);

  const getContentForResponse = useCallback(
    (currentResponse: ClientReviewResponse | undefined, index: number | undefined, isPreview?: boolean) => {
      if (!currentResponse || !responses || index === undefined) {
        return <div className={clsx(style.container)} key="undefined" />;
      }

      const { postDocumentID, media } = currentResponse;
      const { cropHints, mainMimeType, imageSources } = media;
      const jpegSources = imageSources[MIME_TYPE_IMAGE_JPEG];

      const largeImagePath = jpegSources[ImageSize.xxLarge];
      if (!largeImagePath) {
        console.error(`Response missing largeImagePath ${postDocumentID}`);
        return <div className={clsx(style.container)} key={postDocumentID} />;
      }

      const cropHint = cropHints && contentAspectRatio ? cropHints[contentAspectRatio] : undefined;

      return (
        <div className={clsx(style.container)} key={postDocumentID}>
          <div className={clsx(style.main)}>
            <MediaViewerMobile
              thumbURL={jpegSources?.[180]}
              url={largeImagePath}
              srcSet={imageSources}
              mimeType={mainMimeType}
              borderRadius={IMAGE_BORDER_RADIUS}
              enableZoom={!isPreview}
              backgroundColor="var(--color-bg-content)"
              onZoomStart={onZoomStart}
              onZoomEnd={onZoomEnd}
              cropHint={cropHint}
            />
          </div>
        </div>
      );
    },
    [responses, contentAspectRatio, onZoomStart, onZoomEnd]
  );

  const isRTL = getIsRTL();

  const carouselChildren = useMemo(() => {
    if (!responses || responses.length === 0) {
      return undefined;
    }

    const children = responses.map((currentResponse, index) => {
      // Display content for responses that are in vicinity of the current response
      if (responseIndex !== undefined) {
        const startIndex = Math.max(responseIndex - 3, 0);
        const endIndex = Math.min(responseIndex + 3, responses.length - 1);
        if (index >= startIndex && index <= endIndex) {
          const showPreview = !(index === responseIndex || (animatingResponseIndex !== undefined && index === animatingResponseIndex));
          return getContentForResponse(currentResponse, index, showPreview);
        }
      }

      // Otherwise display empty placeholder
      return <div className={clsx(style.container)} key={currentResponse.postDocumentID} />;
    });

    return children;
  }, [animatingResponseIndex, getContentForResponse, responseIndex, responses]);

  const onButtonClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const renderArrow = useCallback(
    ({ type, onClick, isEdge }: RenderArrowProps) => (
      <CarouselArrow
        type={type}
        onClick={onClick}
        hidden={controlsHidden || isEdge || responseIndex === undefined || !(responseIndex === initialResponseIndex && !didChangeIndex)}
      />
    ),
    [controlsHidden, responseIndex, initialResponseIndex, didChangeIndex]
  );

  const [videoScroll, setVideoScroll] = useState<Movement>({ x: 0, y: 0 });

  const updateVideoScroll = useCallback((movement: Movement) => {
    setVideoScroll(movement);

    const id = requestAnimationFrame(() => {
      const videoOverlay = videoOverlayRef.current;
      if (!videoOverlay) {
        return;
      }

      videoOverlay.style.transform = `translateY(${movement.y}px)`;
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const onVideoPointerDown = useCallback(() => {
    updateVideoScroll({ x: 0, y: 0 });
  }, [updateVideoScroll]);

  const onVideoPointerUp = useCallback(() => {
    updateVideoScroll({ x: 0, y: 0 });
  }, [updateVideoScroll]);

  const onVideoDragMove = useCallback((movement: Movement) => updateVideoScroll(movement), [updateVideoScroll]);

  const showingContentPlaceholder = useMemo(() => !carouselChildren || topHeight === 0, [carouselChildren, topHeight]);

  const videoPlayerOverlay = useMemo(() => {
    if (!response) {
      return null;
    }
    const { media } = response;
    const { cropHints, mainMimeType, imageSources, videoSources } = media;
    const jpegSources = imageSources[MIME_TYPE_IMAGE_JPEG];
    const videoPath = videoSources?.[MIME_TYPE_VIDEO_MP4]?.['1920'];
    const cropHint = cropHints && contentAspectRatio ? cropHints[contentAspectRatio] : undefined;

    return videoPath ? (
      <div className={clsx(style.videoOverlay, { [style.visible]: animatingResponseIndex === undefined })} ref={videoOverlayRef}>
        <DragMove
          onDragMove={onVideoDragMove}
          onPointerUp={onVideoPointerUp}
          onPointerCancel={onVideoPointerUp}
          onPointerDown={onVideoPointerDown}
          draggingEnabled
          fullSize
        >
          <VideoDisplay
            url={videoPath}
            coverURL={jpegSources?.[180]}
            mimeType={mainMimeType}
            cropHint={cropHint}
            onControlsHide={onVideoControlsHide}
            borderRadius={IMAGE_BORDER_RADIUS}
            showLoadingSpinner
            noBackground
          />
        </DragMove>
      </div>
    ) : null;
  }, [animatingResponseIndex, contentAspectRatio, onVideoControlsHide, onVideoDragMove, onVideoPointerDown, onVideoPointerUp, response]);

  const innerMainContent = useMemo(() => {
    if (!response) {
      return null;
    }
    const { postDocumentID, media } = response;
    const { imageSources } = media;
    const jpegSources = imageSources[MIME_TYPE_IMAGE_JPEG];

    const largeImagePath = jpegSources[ImageSize.xxLarge];
    if (!largeImagePath) {
      console.error(`Response missing largeImagePath ${postDocumentID}`);
      return <div className={clsx(style.container)} key={postDocumentID} />;
    }

    return showingContentPlaceholder || !carouselChildren ? (
      <div className={clsx(style.placeholder)}>{getContentForResponse(response, responseIndex, true)}</div>
    ) : (
      <Carousel
        initialIndex={responseIndex}
        onDidAnimateToIndex={onDidChangeIndex}
        onWillAnimateToIndex={onWillChangeIndex}
        disableScrolling={controlsHidden}
        renderArrow={renderArrow}
        itemLength={topHeight}
        transitionDuration={350}
        externalScroll={videoScroll}
      >
        {carouselChildren}
      </Carousel>
    );
  }, [
    carouselChildren,
    controlsHidden,
    getContentForResponse,
    onDidChangeIndex,
    onWillChangeIndex,
    renderArrow,
    response,
    responseIndex,
    showingContentPlaceholder,
    topHeight,
    videoScroll,
  ]);

  const [showProductsModal, setShowProductsModal] = useState(false);
  const toggleProductsModal = useCallback((show: boolean) => () => setShowProductsModal(show), []);

  const productBanner = useMemo(() => {
    const hasProduct = !!response && response?.products.length > 0;
    if (!hasProduct) {
      return null;
    }

    const responseProducts = response.products;

    return (
      <OverlayIcon
        text={t('LOC_FEATURED_PRODUCTS', { count: responseProducts.length })}
        onClick={toggleProductsModal(true)}
        customIcon
        icon={
          <div className={clsx(style.productBanner)}>
            <ProductsBannerMini products={products} hasProduct size="large" />
          </div>
        }
      />
    );
  }, [products, response, t, toggleProductsModal]);

  const mainContent = useMemo(() => {
    const thumbURL = response?.media.imageSources[MIME_TYPE_IMAGE_JPEG][180];
    const mirror = thumbURL ? (
      <div className={clsx(style.mirror)}>
        <ImageDisplay url={thumbURL} borderRadius={IMAGE_BORDER_RADIUS} keepPreviousUntilLoaded />
      </div>
    ) : null;

    return (
      <div className={clsx(style.mainContent)}>
        {innerMainContent}
        {videoPlayerOverlay}
        {mirror}
        <div className={clsx(style.control, style.close, { [style.rtl]: isRTL }, { [style.hidden]: controlsHidden })}>
          <Button content={<X />} plain onClick={onButtonClose} ariaLabel="close" />
        </div>
        <div className={clsx(style.bottomControls, { [style.rtl]: isRTL, [style.safeAreaPadded]: reviewContentIsEmpty })}>
          {hasSizeRecAvailable && singleResponseProduct ? (
            <div className={clsx(style.control, { [style.rtl]: isRTL }, { [style.hidden]: controlsHidden })}>
              <SizeRecIcon onClick={onActiveMatchingTypeToggle('size-rec')} />
            </div>
          ) : null}
          {likesEnabled ? (
            <div className={clsx(style.control, { [style.rtl]: isRTL }, { [style.hidden]: controlsHidden })}>
              <PostLikeHeart response={response} />
            </div>
          ) : null}
          {addToBagEnabled && singleResponseProduct ? (
            <div className={clsx(style.control, { [style.rtl]: isRTL }, { [style.hidden]: controlsHidden })}>
              <AddToBag product={singleResponseProduct ?? undefined} response={response} />
            </div>
          ) : null}
          <div className={clsx(style.control, { [style.hidden]: controlsHidden })}>{productBanner}</div>
        </div>
      </div>
    );
  }, [
    response,
    innerMainContent,
    videoPlayerOverlay,
    isRTL,
    controlsHidden,
    onButtonClose,
    reviewContentIsEmpty,
    hasSizeRecAvailable,
    singleResponseProduct,
    onActiveMatchingTypeToggle,
    likesEnabled,
    addToBagEnabled,
    productBanner,
  ]);

  const content = useMemo(() => {
    const revealDragEnabled = contentScrolledToTop || !contentIsScrollable || revealCollapsed;
    if (responses && responses.length > 0 && response) {
      return (
        <Reveal
          main={mainContent}
          revealID={`id-${response.postDocumentID}-${response.type.valueOf()}`}
          reveal={
            <ReviewDetails
              response={response}
              products={products}
              onBeginUserMatchInfo={onBeginUserMatchInfo}
              headerMarginRight={27}
              showBeginUserMatchInfoSection={showBeginUserMatchInfoSection}
              showResetUserButton={showResetUserButton}
              anyResponseHasMatch={anyResponseHasMatch}
              showFooter
              scrollEnabled={!revealCollapsed}
              onIsScrollable={onContentIsScrollable}
              onScrollToTop={onContentScrollTop}
              onContentIsEmpty={onReviewContentIsEmpty}
            />
          }
          onBottomDragStart={onBottomDragStart}
          onBottomDragEnd={onBottomDragEnd}
          revealDragEnabled={revealDragEnabled}
          onTopHeightChange={onTopHeightChange}
          aspectRatio={revealAspectRatio}
          keepAspectRatioConstraint={!reviewContentIsEmpty}
        />
      );
    }
    if (responses === undefined) {
      return (
        <div>
          <Spinner size={SpinnerSize.medium} positioning={SpinnerPositioning.transformRelative} />
        </div>
      );
    }
    return (
      <div>
        <NoResultsPlaceholder />
      </div>
    );
  }, [
    contentScrolledToTop,
    contentIsScrollable,
    revealCollapsed,
    responses,
    response,
    mainContent,
    products,
    onBeginUserMatchInfo,
    showBeginUserMatchInfoSection,
    showResetUserButton,
    anyResponseHasMatch,
    onContentIsScrollable,
    onContentScrollTop,
    onReviewContentIsEmpty,
    onBottomDragStart,
    onBottomDragEnd,
    onTopHeightChange,
    revealAspectRatio,
    reviewContentIsEmpty,
  ]);

  const sizeRecPortal = (
    <CarouselMobileSizeRecModal
      activeMatchingType={activeMatchingType}
      defaultMatchingType={defaultMatchingType}
      hasContentMatchAvailable={hasContentMatchAvailable}
      hasSizeRecAvailable={hasSizeRecAvailable}
      sizeRecommendation={sizeRecommendation}
      onActiveMatchingTypeChange={onActiveMatchingTypeChange}
      responses={responses}
    />
  );

  const productsModal = (
    <CarouselMobileProductsModal
      response={response}
      products={products}
      onProductLinkClick={onProductLinkClick}
      open={showProductsModal}
      onClose={toggleProductsModal(false)}
    />
  );

  return (
    // eslint-disable-next-line react/jsx-curly-brace-presence
    <div
      className={clsx(style.root)}
      style={
        {
          '--color-text-secondary': '#505050',
          '--bottom-control-padding': `${videoControlsVisible ? videoControlProgressBarHeight : 8}px`,
        } as CustomCSSProperties
      }
    >
      {content}
      {productsModal}
      {sizeRecPortal}
    </div>
  );
};
